// The app's own model of the API's data. Repositories map generated DTOs into these, so screens never
// see the wire format. Enum values keep the API's spelling; `unknown` stands for values this version
// does not know yet.

export type ItemType = 'approval' | 'screening' | 'report' | 'task' | 'unknown';
export type ItemStatus =
  | 'pending'
  | 'changes_requested'
  | 'approved'
  | 'rejected'
  | 'completed'
  | 'failed'
  | 'withdrawn'
  | 'expired'
  | 'unknown';
export type Execution = 'none' | 'manual' | 'source' | 'unknown';
export type Priority = 'low' | 'normal' | 'high';
export type DecisionKind = 'approve' | 'reject' | 'request_changes';
export type ActorType = 'user' | 'api_key' | 'system';
export type Role = 'owner' | 'approver' | 'viewer';

/** What the work is, from the server (`Item.kind`); lists group by it. */
export type WorkKind =
  | 'request'
  | 'report'
  | 'task'
  | 'offer'
  | 'deliverable'
  | 'access_request'
  | 'reply'
  | 'message'
  | 'post'
  | 'group_post'
  | 'ad'
  | 'document'
  | 'other';

/** What the signed-in person may do with an item now, from the server (`Item.actions`). */
export type ItemAction = 'approve' | 'reject' | 'request_changes' | 'edit' | 'hand_off' | 'report_result';

/** What the signed-in person may do in the organization, from the server (`Me.actions`). */
export type SessionAction = 'request_employee';

export interface Session {
  userId: string;
  name: string;
  email: string;
  organizationName: string;
  role: Role;
  actions: ReadonlySet<SessionAction>;
}

export const firstName = (s: Session) => s.name.split(' ')[0] ?? s.name;
/** May ask the Talkos team for a new employee; the server decides. */
export const canRequestEmployee = (s: Session | null | undefined) => s?.actions.has('request_employee') ?? false;

export interface Actor {
  type: ActorType;
  name: string;
}

export interface Decision {
  kind: DecisionKind;
  /** Approved without a person, because the employee works on its own; `decidedBy` is the system. */
  automatic: boolean;
  decidedBy: Actor;
  decidedAt: Date;
  comment?: string;
}

export interface ItemResult {
  succeeded: boolean;
  reportedBy: Actor;
  reportedAt: Date;
  link?: string;
  note?: string;
}

export interface ItemLink {
  label: string;
  url: string;
}

// ---- Team ----

/** One business of the organization. */
export interface Workspace {
  id: string;
  slug: string;
  name: string;
  description: string;
  color: string;
  openCount: number;
  employeeCount: number;
}

export interface WorkspaceSummary {
  id: string;
  slug: string;
  name: string;
  color: string;
}

/** An AI employee: a role in a workspace with a face (name, avatar, color). */
export interface Employee {
  id: string;
  workspaceId: string;
  role: string;
  name: string;
  title: string;
  description: string;
  avatar: string;
  color: string;
  active: boolean;
  /** Items waiting for a person. */
  openCount: number;
  /** Approved work the employee is carrying out now, tasks included. */
  inProgressCount: number;
  /** People can hand work to this employee. */
  takesTasks: boolean;
  /** The kinds of work this employee does, in display order, and which are approved without a person. */
  autonomy: KindAutonomy[];
  /** The signed-in person may change `autonomy`; the server decides (`Employee.actions`). */
  canSetAutonomy: boolean;
  /** When and by whom any kind's autonomy was last changed. */
  autonomyChangedAt?: Date;
  autonomyChangedBy?: string;
  /** What the business told the employee (ADR 0010). */
  profile: EmployeeProfile;
  /** The signed-in person may change `profile`; the server decides (`Employee.actions`). */
  canEditProfile: boolean;
  lastActivityAt?: Date;
}

/** What a business told an employee: answers to its module's questions (by question key), the
 * connections it works through (by key) and a free note. */
export interface EmployeeProfile {
  answers: Record<string, string>;
  connections: string[];
  note?: string;
}

export const isProfileEmpty = (p: EmployeeProfile) =>
  Object.values(p.answers).every((a) => a.trim() === '') && p.connections.length === 0 && !p.note?.trim();

// ---- Hiring (ADR 0010) ----

export type ModuleCategory = 'customers' | 'marketing' | 'sales' | 'admin' | 'tech';
export type QuestionKind = 'text' | 'long' | 'url' | 'choice';

/** Something an employee asks the business when hired; the answer goes into its profile. */
export interface ModuleQuestion {
  key: string;
  label: string;
  help?: string;
  kind: QuestionKind;
  /** For `choice`. */
  options: string[];
  required: boolean;
}

/** A channel or tool an employee works through (email, a Facebook Page, a calendar). */
export interface ModuleConnection {
  key: string;
  label: string;
}

/** An employee Talkos offers, the same for every customer. Texts are Lithuanian data. */
export interface EmployeeModule {
  role: string;
  name: string;
  title: string;
  summary: string;
  description: string;
  avatar: string;
  color: string;
  category: ModuleCategory;
  kinds: WorkKind[];
  /** Which of `kinds` may be approved without a person from the start. */
  automatable: AutonomyKind[];
  skills: string[];
  connections: ModuleConnection[];
  industries: string[];
  questions: ModuleQuestion[];
  takesTasks: boolean;
}

export type RequestStatus = 'requested' | 'in_progress' | 'done' | 'declined' | 'unknown';

/** A business asking the Talkos team for a new employee. */
export interface EmployeeRequest {
  id: string;
  /** The module asked for; none for an employee the business described itself. */
  role?: string;
  title: string;
  workspaceId?: string;
  /** A new business, when no workspace is named. */
  workspaceName?: string;
  /** What the new project is, as the person described it. */
  workspaceDescription?: string;
  profile: EmployeeProfile;
  automaticKinds: AutonomyKind[];
  status: RequestStatus;
  /** Who asked. */
  requestedBy: string;
  /** The employee hired for it, once done. */
  employeeId?: string;
  createdAt: Date;
  updatedAt: Date;
}

/** Still waiting on the Talkos team. */
export const isOpenRequest = (r: EmployeeRequest) => r.status === 'requested' || r.status === 'in_progress';

/** What the hiring form sends: a module (`role`) or, for an employee of one's own, a description of the
 * work in `profile.note`; and an existing workspace or a new one's name and description. */
export interface EmployeeRequestInput {
  role?: string;
  title?: string;
  workspaceId?: string;
  workspaceName?: string;
  workspaceDescription?: string;
  profile: EmployeeProfile;
  automaticKinds: AutonomyKind[];
}

/** The kinds of work autonomy is set for: every kind but tasks, which a person hands over. */
export type AutonomyKind = Exclude<WorkKind, 'task'>;

/** Whether new work of one kind is approved without a person (requests: let in after the check). */
export interface KindAutonomy {
  kind: AutonomyKind;
  automatic: boolean;
  /** It may be made automatic: the employee carries this work out itself (ADR 0009). */
  allowed: boolean;
}

/** The kinds approved without a person. */
export const automaticKinds = (e: Pick<Employee, 'autonomy'>) => e.autonomy.filter((k) => k.automatic);

/** Every kind that may be automatic is, and there is at least one: the employee works on its own. */
export function allAllowedAutomatic(e: Pick<Employee, 'autonomy'>): boolean {
  const allowed = e.autonomy.filter((k) => k.allowed);
  return allowed.length > 0 && allowed.every((k) => k.automatic);
}

export interface EmployeeSummary {
  id: string;
  role: string;
  name: string;
  title: string;
  avatar: string;
  color: string;
}

// ---- Payloads ----

export interface Contact {
  name?: string;
  email?: string;
  company?: string;
  phone?: string;
}

export interface PriceLine {
  key: string;
  label: string;
  amount: number;
  monthly: boolean;
  /** An alternative or add-on the client chooses; not part of the total. */
  optional: boolean;
}

export interface Metric {
  label: string;
  value: string;
  delta?: string;
}

export type AdPlatform = 'facebook' | 'instagram' | 'google' | 'tiktok' | 'linkedin' | 'other';

/** A budget change; `from` is missing for a new campaign. */
export interface AdBudget {
  from?: number;
  to: number;
  currency: string;
  period: 'day' | 'month' | 'total';
}

export interface ChecklistEntry {
  text: string;
  done: boolean;
}

/** The item a task came from, as the person saw it when handing it off. */
export interface TaskSource {
  itemId: string;
  type: string;
  schema: string;
  title: string;
  payload: ItemPayload;
  summary?: string;
  externalRef?: string;
  employee?: string;
}

type Raw = Record<string, unknown>;

/** The content of an item, typed by its schema. Each variant keeps the original JSON in `raw`, so
 * fields the app does not know survive an edit. */
export type ItemPayload =
  | {
      type: 'social_post';
      raw: Raw;
      channel: string;
      text: string;
      language?: string;
      target?: string;
      targetUrl?: string;
      publishAt?: Date;
      headline?: string;
      link?: string;
      firstComment?: string;
      notes?: string;
      media: string[];
    }
  | {
      type: 'message_reply';
      raw: Raw;
      channel: string;
      to: Contact;
      text: string;
      subject?: string;
      inReplyTo?: string;
      notes?: string;
    }
  | {
      type: 'offer';
      raw: Raw;
      client: Contact;
      title: string;
      summary: string;
      prices: PriceLine[];
      currency: string;
      scope: string[];
      outOfScope: string[];
      acceptanceCriteria: string[];
      message?: string;
      notes?: string;
    }
  | {
      type: 'inbound_message';
      raw: Raw;
      channel: string;
      from: Contact;
      text: string;
      subject?: string;
      receivedAt?: Date;
      context: Raw;
    }
  | {
      type: 'deliverable';
      raw: Raw;
      title: string;
      url: string;
      summary?: string;
      checklist: ChecklistEntry[];
      client: Contact;
      message?: string;
      notes?: string;
    }
  | { type: 'task'; raw: Raw; source: TaskSource; note?: string }
  | {
      type: 'access_request';
      raw: Raw;
      resource: string;
      purpose: string;
      target: string;
      kind?: string;
      requestedBy?: string;
      validMinutes?: number;
      notes?: string;
    }
  | { type: 'report'; raw: Raw; text: string; period?: string; metrics: Metric[]; suggestHandoff: boolean }
  | {
      type: 'ad_change';
      raw: Raw;
      platform: AdPlatform;
      campaign: string;
      /** What to do, in a sentence: the headline. */
      change: string;
      reason?: string;
      budget?: AdBudget;
      adText?: string;
      expected?: string;
      metrics: Metric[];
    }
  | { type: 'document'; raw: Raw; title: string; body: string; url?: string; for: Contact }
  | { type: 'hidden' }
  | { type: 'unknown'; raw: Raw };

export type PayloadOf<T extends ItemPayload['type']> = Extract<ItemPayload, { type: T }>;

// ---- Items ----

/** Something an AI employee needs a person for. */
export interface Item {
  id: string;
  workspace: WorkspaceSummary;
  employee: EmployeeSummary;
  type: ItemType;
  kind: WorkKind;
  /** What the signed-in person may do with it now; the server decides. */
  actions: ReadonlySet<ItemAction>;
  /** Approving sends it outside the organization or releases a secret: confirm first. */
  outward: boolean;
  schema: string;
  status: ItemStatus;
  execution: Execution;
  priority: Priority;
  title: string;
  summary?: string;
  revision: number;
  payload: ItemPayload;
  externalRef?: string;
  /** For a task, the item it was handed off from; for an employee's own item, the task it answers. */
  parentId?: string;
  links: ItemLink[];
  decision?: Decision;
  result?: ItemResult;
  expiresAt?: Date;
  /** For content from outside: what the isolated check made of it (ADR 0011), once it ran. */
  check?: ItemCheck;
  createdAt: Date;
  updatedAt: Date;
  /** Version tag; deciding sends it, so a version nobody saw is never approved. */
  etag?: string;
}

export const can = (i: Item, a: ItemAction) => i.actions.has(a);
export const needsDecision = (i: Item) => i.status === 'pending';
/** Approved work a person still has to carry out (for example publish the post). Whether this person
 * may report it done is `can(item, 'report_result')`. */
export const awaitsManualResult = (i: Item) => i.status === 'approved' && i.execution === 'manual';
export const isTask = (i: Item) => i.type === 'task';
/** A task the employee is carrying out right now. */
export const isWorking = (i: Item) => i.type === 'task' && i.status === 'approved';
export const isUntrusted = (i: Item) => i.type === 'screening';

/** What the isolated check thinks of a message from outside; values this version does not know read
 * as `unclear`. */
export type CheckVerdict = 'ok' | 'spam' | 'injection' | 'abuse' | 'unclear';

export interface ItemCheck {
  verdict: CheckVerdict;
  /** Why, for a person (Lithuanian, from the server). */
  reason: string;
  /** `rules` or `ai`. */
  by: string;
}
/** Content the person may change before approving: the server allows it and the app can draw it. */
export const isEditable = (i: Item) =>
  can(i, 'edit') && i.payload.type !== 'hidden' && i.payload.type !== 'unknown';
/** A post the employee publishes itself once approved, so approving is publishing. */
export const publishesOnApproval = (i: Item) =>
  (i.kind === 'post' || i.kind === 'group_post') && i.execution === 'source';

// ---- History ----

export type EventKind =
  | 'created'
  | 'revised'
  | 'approved'
  | 'rejected'
  | 'changes_requested'
  | 'completed'
  | 'failed'
  | 'withdrawn'
  | 'expired'
  | 'handed_off'
  | 'unknown';

/** One line of the audit trail. */
export interface ItemEvent {
  id: string;
  itemId: string;
  itemTitle: string;
  workspaceId: string;
  kind: EventKind;
  actor: Actor;
  createdAt: Date;
  employee?: EmployeeSummary;
  data: Raw;
}

/** The system's own "done" right after an approval of work with nothing to execute (a message let
 * in, an approved note): the approval already says it, so lists leave it out. */
export const echoesApproval = (e: ItemEvent) => e.kind === 'completed' && e.actor.type === 'system';

const str = (v: unknown) => (typeof v === 'string' ? v : undefined);
export const eventComment = (e: ItemEvent) => str(e.data.comment);
export const eventNote = (e: ItemEvent) => str(e.data.note);
export const eventLink = (e: ItemEvent) => str(e.data.link);
export const eventReason = (e: ItemEvent) => str(e.data.reason);
export const eventRevision = (e: ItemEvent) => (typeof e.data.revision === 'number' ? e.data.revision : undefined);
export const editedByPerson = (e: ItemEvent) => e.data.edited_by_person === true;
/** For `approved`: nobody approved it; the employee works on its own. */
export const approvedAutomatically = (e: ItemEvent) => e.data.automatic === true;
/** For `approved`: content from outside let in without a person, after the isolated check passed it. */
export const letInAfterCheck = (e: ItemEvent) => e.data.automatic === true && e.data.checked === true;
/** For `handed_off`: the new task and who took it. */
export const eventTaskId = (e: ItemEvent) => str(e.data.task_id);
export const handedTo = (e: ItemEvent) => str(e.data.employee);

export interface Page<T> {
  items: T[];
  nextCursor?: string;
}
